import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getBestAvailableUrl } from '@components/shared/OptimizedImage';
import { prepareDesktopSlidesData, preparePhoneSlidesData } from '@components/shared/ImageViewer/imageViewerUtils';
import { useSlideshowExperience } from '@components/shared/ImageViewer/hooks/useSlideshowExperience';
import { frontSettings, frontSide, hasOffset, updateRawImageSettings } from '@components/shared/spreadLayoutUtils';
import SpreadLayerIcon from '@components/shared/SpreadLayerIcon';
import AddBoxButtons from '@components/shared/AddBoxButtons';
import { useAnyBlockEditing } from '@components/preview/editGates';
import OutsideFrame, { frameGeometry, rectInFrame, useFrameHost } from '@components/preview/outsideFrame';
import { BLOCK_SELECT_MESSAGE, EDITORIAL_GALLERY_MESSAGE } from '@/blocks/preview';
import { addBox, boxSide, boxesOf, createBlankSpread, createPaper, mergeBoxes, updateSlideBoxes, withBoxes } from '@/slideshow/boxes';

const TEXT_EDITOR_UI = '.editor-toolbar, [data-editor-popover]';

/** True while the caret is in the slideshow text, or its floating toolbar. */
function useSlideshowTextFocused() {
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        let busy = false;
        const inText = (el) => Boolean(el?.closest?.('[data-slideshow-text]'));
        const inUi = (el) => Boolean(el?.closest?.(TEXT_EDITOR_UI));
        const read = () => {
            // focusout then focusin in one click; read after both have settled.
            requestAnimationFrame(() => {
                const active = document.activeElement;
                if (inText(active)) busy = true;
                else if (!(busy && inUi(active))) busy = false;
                setFocused(busy);
            });
        };
        read();
        document.addEventListener('focusin', read);
        document.addEventListener('focusout', read);
        return () => {
            document.removeEventListener('focusin', read);
            document.removeEventListener('focusout', read);
        };
    }, []);
    return focused;
}

export function postEditorial(payload) {
    if (typeof window === 'undefined' || window.parent === window) return;
    window.parent.postMessage({ type: EDITORIAL_GALLERY_MESSAGE, ...payload }, window.location.origin);
}

function thumbUrl(file) {
    if (!file) return '';
    const src = getBestAvailableUrl(file, 480) || getBestAvailableUrl(file, 768) || file?.url?.original || '';
    if (!src) return '';
    // Stored paths are site-root paths without the leading slash; the strip may be drawn in the admin page.
    const rooted = /^([a-z][a-z0-9+.-]*:|\/)/i.test(src) ? src : `/${src}`;
    return new URL(rooted, window.location.origin).href;
}

/** A frame's picture, a plain sheet for a blank page, or the phone text page. */
function Thumb({ side, className, style }) {
    if (side?.type === 'reading') {
        return (
            <span aria-label="Text page" className={`${className} flex items-center justify-center bg-white text-[10px] font-medium text-gray-500`} style={style}>
                Aa
            </span>
        );
    }
    if (side?.paper || side?.type === 'paper') {
        return <span aria-label="Blank page" className={`${className} block bg-white`} style={style} />;
    }
    return <img src={thumbUrl(side?.file)} alt="" className={className} style={style} draggable={false} />;
}

Thumb.propTypes = {
    side: PropTypes.object,
    className: PropTypes.string,
    style: PropTypes.object,
};

function isSpreadEntry(item) {
    return Array.isArray(item) && item.length === 2;
}

function pairAt(images, index) {
    if (!Array.isArray(images) || index < 0 || index >= images.length - 1) return null;
    const left = images[index];
    const right = images[index + 1];
    if (isSpreadEntry(left) || isSpreadEntry(right)) return null;
    const next = [...images];
    next.splice(index, 2, [withBoxes(left, mergeBoxes(left, right)), withBoxes(right, [])]);
    return next;
}

function unpairAt(images, index) {
    if (!Array.isArray(images) || index < 0 || index >= images.length) return null;
    const item = images[index];
    if (!isSpreadEntry(item)) return null;
    const next = [...images];
    next.splice(index, 1, ...item);
    return next;
}

/** Space between the strip and the edges of the content area. */
const EDGE = 12;
/** Where the user dragged the strip, from its spot at the bottom. Kept while it hides for text editing. */
let savedNudge = { x: 0, y: 0 };

function windowArea() {
    return { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
}

/** The admin's preview area (the grey well around the page), in the preview's coordinates. */
function useContentArea(host) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const bump = () => setTick((tick) => tick + 1);
        const views = [...new Set([window, host?.doc.defaultView].filter(Boolean))];
        views.forEach((view) => {
            view.addEventListener('resize', bump);
            view.addEventListener('scroll', bump, true);
        });
        const well = host?.frame.closest('.admin-studio__well');
        const Observer = host?.doc.defaultView.ResizeObserver;
        const observer = well && Observer ? new Observer(bump) : null;
        if (observer) {
            observer.observe(well);
            observer.observe(host.frame);
        }
        return () => {
            views.forEach((view) => {
                view.removeEventListener('resize', bump);
                view.removeEventListener('scroll', bump, true);
            });
            observer?.disconnect();
        };
    }, [host]);
    if (typeof window === 'undefined') return null;
    const well = host?.frame.closest('.admin-studio__well');
    return well ? rectInFrame(host, well.getBoundingClientRect()) : windowArea();
}

/** True while the admin page has a dialog open; the strip's layer would sit over it. */
function useAdminDialogOpen(host) {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const doc = host?.doc;
        if (!doc) return undefined;
        const check = () => setOpen(Boolean(doc.querySelector('[role="dialog"]')));
        check();
        const Observer = doc.defaultView.MutationObserver;
        const observer = new Observer(check);
        observer.observe(doc.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [host]);
    return open;
}

function clampTo(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

/**
 * Appearance-only filmstrip for editorial (slideshow) pages.
 * Select a frame, drag to reorder, pair/unpair spreads, or open Arrange.
 */
export default function EditorialFilmstrip({
    images,
    files,
    pagePath,
    pages,
    currentImageId,
    currentReading = false,
    onSelect,
}) {
    const experience = useSlideshowExperience();
    // On a phone a slide breaks into pages (its text page among them); the strip shows those.
    const phonePages = useMemo(() => {
        if (!experience.phoneLayout) return null;
        const byEntry = new Map();
        preparePhoneSlidesData(images || [], files || {}, pagePath || null, pages || {}).forEach((part) => {
            if (part.rawIndex == null) return;
            byEntry.set(part.rawIndex, [...(byEntry.get(part.rawIndex) || []), part]);
        });
        return byEntry;
    }, [experience.phoneLayout, images, files, pagePath, pages]);
    const slides = useMemo(
        () => prepareDesktopSlidesData(images || [], files || {}, pagePath || null, pages || {}).filter(Boolean),
        [images, files, pagePath, pages],
    );
    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);
    const [extraIndex, setExtraIndex] = useState(null);
    const dragRef = useRef(null);
    const host = useFrameHost();
    const area = useContentArea(host);
    const dialogOpen = useAdminDialogOpen(host);
    const barRef = useRef(null);
    const moveRef = useRef(null);
    const [barSize, setBarSize] = useState(null);
    const [nudge, setNudge] = useState(savedNudge);
    useLayoutEffect(() => {
        const bar = barRef.current;
        if (!bar) return undefined;
        const measure = () => setBarSize((prev) => (
            prev && prev.w === bar.offsetWidth && prev.h === bar.offsetHeight
                ? prev
                : { w: bar.offsetWidth, h: bar.offsetHeight }
        ));
        measure();
        const Observer = bar.ownerDocument.defaultView.ResizeObserver;
        const observer = new Observer(measure);
        observer.observe(bar);
        return () => observer.disconnect();
    });
    // The strip sits over the bottom of the text; hide it while that text (or
    // any other block) is being edited so the caret and selection stay reachable.
    const editingBlock = useAnyBlockEditing();
    const textFocused = useSlideshowTextFocused();

    if (!slides.length || editingBlock || textFocused || dialogOpen) return null;

    const activeIndex = slides.findIndex((slide) => {
        if (slide.type === 'spread') return slide.images.some((img) => img.id === currentImageId);
        return slide.id === currentImageId;
    });

    const activeSlide = activeIndex >= 0 ? slides[activeIndex] : null;
    const isActiveSpread = activeSlide?.type === 'spread';
    const canPairWithNext = activeIndex >= 0
        && activeSlide?.type !== 'spread'
        && slides[activeIndex + 1]
        && slides[activeIndex + 1].type !== 'spread';
    const selectedPair = (() => {
        if (extraIndex == null || activeIndex < 0) return null;
        const a = Math.min(activeIndex, extraIndex);
        const b = Math.max(activeIndex, extraIndex);
        if (b - a !== 1) return null;
        if (slides[a]?.type === 'spread' || slides[b]?.type === 'spread') return null;
        return a;
    })();
    const canPair = selectedPair != null || canPairWithNext;
    const pairIndex = selectedPair != null ? selectedPair : (canPairWithNext ? activeIndex : -1);
    // Which image is on top only matters once one is moved over the other page.
    const spreadSides = isActiveSpread ? activeSlide.images : null;
    const overlapping = Boolean(spreadSides?.some(hasOffset));
    const onTop = overlapping ? frontSide(spreadSides) : 1;
    const [topName, otherName] = onTop === 0 ? ['left', 'right'] : ['right', 'left'];

    const finish = () => {
        const from = dragRef.current;
        const to = overIndex;
        dragRef.current = null;
        setDragIndex(null);
        setOverIndex(null);
        if (from == null || to == null || from === to) return;
        const next = [...(images || [])];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        postEditorial({ action: 'order', images: next });
    };

    const publishImages = (next) => {
        if (!next) return;
        setExtraIndex(null);
        postEditorial({ action: 'order', images: next });
    };

    const boxSlide = activeSlide && activeSlide.type !== 'video' && activeSlide.rawIndex != null ? activeSlide : null;
    const slideBoxes = boxSlide ? boxesOf((images || [])[boxSlide.rawIndex]) : [];
    const addSlideBox = (type) => {
        const boxes = addBox(slideBoxes, type, {
            single: boxSlide.type !== 'spread',
            side: boxSide((images || [])[boxSlide.rawIndex]),
        });
        if (boxes === slideBoxes) return;
        publishImages(updateSlideBoxes(images, boxSlide.rawIndex, () => boxes));
        window.parent.postMessage({
            type: BLOCK_SELECT_MESSAGE,
            blockId: boxes[boxes.length - 1].block.id,
            tileId: '',
            tileKind: '',
        }, window.location.origin);
    };

    const addBlankPage = () => {
        const list = images || [];
        const at = boxSlide ? boxSlide.rawIndex + 1 : list.length;
        publishImages([...list.slice(0, at), createPaper(), ...list.slice(at)]);
    };

    const addBlankSpread = () => {
        const list = images || [];
        const at = boxSlide ? boxSlide.rawIndex + 1 : list.length;
        publishImages([...list.slice(0, at), createBlankSpread(), ...list.slice(at)]);
    };

    const putOnTop = (side) => publishImages(frontSettings(side).reduce(
        (list, settings, index) => updateRawImageSettings(list, spreadSides[index].id, settings),
        images || [],
    ));

    // Pinned to the bottom of the content area; dragging moves it anywhere inside it.
    const space = area || windowArea();
    const size = barSize || { w: 0, h: 0 };
    const home = {
        x: (space.left + space.right) / 2 - size.w / 2,
        y: space.bottom - size.h - EDGE,
    };
    const place = (offset) => ({
        x: clampTo(home.x + offset.x, space.left + EDGE, space.right - size.w - EDGE),
        y: clampTo(home.y + offset.y, space.top + EDGE, space.bottom - size.h - EDGE),
    });
    const at = place(nudge);

    const startMove = (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        try {
            event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
            // The pointer is already gone.
        }
        moveRef.current = {
            x: event.clientX,
            y: event.clientY,
            from: { x: at.x - home.x, y: at.y - home.y },
            scale: host ? frameGeometry(host).scale : 1,
        };
    };
    const onMove = (event) => {
        const move = moveRef.current;
        if (!move) return;
        const next = {
            x: move.from.x + (event.clientX - move.x) / move.scale,
            y: move.from.y + (event.clientY - move.y) / move.scale,
        };
        const placed = place(next);
        setNudge({ x: placed.x - home.x, y: placed.y - home.y });
    };
    const endMove = () => {
        if (!moveRef.current) return;
        moveRef.current = null;
        savedNudge = nudge;
    };
    const resetMove = () => {
        savedNudge = { x: 0, y: 0 };
        setNudge(savedNudge);
    };

    return (
        <OutsideFrame>
        <div
            ref={barRef}
            data-editorial-filmstrip
            className="pointer-events-auto fixed z-[10050] flex items-end gap-2 rounded-xl border border-purple-200 bg-white/95 p-2 shadow-lg backdrop-blur"
            style={{
                left: at.x,
                top: at.y,
                maxWidth: Math.min(980, space.right - space.left - EDGE * 2),
                visibility: barSize ? 'visible' : 'hidden',
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            <div
                role="button"
                tabIndex={-1}
                aria-label="Move the strip"
                title="Drag to move. Double-click to put it back at the bottom."
                className="flex w-3 shrink-0 cursor-grab touch-none select-none items-center justify-center self-stretch rounded text-gray-300 hover:bg-gray-100 hover:text-gray-500 active:cursor-grabbing"
                onPointerDown={startMove}
                onPointerMove={onMove}
                onPointerUp={endMove}
                onPointerCancel={endMove}
                onDoubleClick={resetMove}
            >
                <svg viewBox="0 0 6 16" className="h-4 w-1.5" fill="currentColor" aria-hidden="true">
                    <circle cx="1.5" cy="3" r="1" /><circle cx="4.5" cy="3" r="1" />
                    <circle cx="1.5" cy="8" r="1" /><circle cx="4.5" cy="8" r="1" />
                    <circle cx="1.5" cy="13" r="1" /><circle cx="4.5" cy="13" r="1" />
                </svg>
            </div>
            <div className="flex max-w-[min(620px,calc(100vw-14rem))] items-end gap-1.5 overflow-x-auto pb-0.5">
                {slides.map((slide, index) => {
                    const active = index === activeIndex;
                    const marked = extraIndex === index;
                    const dropMark = overIndex === index && dragIndex != null && dragIndex !== index;
                    const left = slide.type === 'spread' ? slide.images[0] : slide;
                    const right = slide.type === 'spread' ? slide.images[1] : null;
                    const selectId = slide.type === 'spread' ? slide.images[0]?.id : slide.id;
                    const parts = phonePages?.get(slide.rawIndex) || null;
                    return (
                        <button
                            key={slide.id || index}
                            type="button"
                            draggable
                            title={
                                slide.type === 'spread'
                                    ? 'Spread — click to select, ⌘/Ctrl-click to multi-select, drag to reorder'
                                    : 'Click to select, ⌘/Ctrl-click with neighbour to pair, drag to reorder'
                            }
                            onDragStart={(event) => {
                                dragRef.current = index;
                                setDragIndex(index);
                                event.dataTransfer.effectAllowed = 'move';
                                event.dataTransfer.setData('text/plain', String(index));
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setOverIndex(index);
                            }}
                            onDrop={(event) => {
                                event.preventDefault();
                                finish();
                            }}
                            onDragEnd={finish}
                            onClick={(event) => {
                                if (event.metaKey || event.ctrlKey) {
                                    if (index === activeIndex) {
                                        setExtraIndex(null);
                                        return;
                                    }
                                    setExtraIndex(index);
                                    return;
                                }
                                setExtraIndex(null);
                                if (selectId) onSelect?.(selectId, slide.url);
                            }}
                            className={`relative flex h-14 shrink-0 overflow-hidden rounded border bg-gray-100 transition ${
                                active ? 'border-purple-600 ring-2 ring-purple-300' : 'border-gray-200 hover:border-purple-300'
                            } ${marked ? 'ring-2 ring-amber-400' : ''} ${dropMark ? 'ring-2 ring-purple-400' : ''} ${dragIndex === index ? 'opacity-40' : ''}`}
                            style={{ width: parts ? Math.max(1, parts.length) * 44 : (right ? 88 : 44) }}
                        >
                            {parts ? parts.map((part, at) => {
                                const shown = active && (part.type === 'reading'
                                    ? currentReading
                                    : !currentReading && part.id === currentImageId);
                                return (
                                    <span
                                        key={part.id}
                                        role="button"
                                        tabIndex={-1}
                                        title={part.type === 'reading' ? 'Text page on phones' : undefined}
                                        onClick={(event) => {
                                            if (event.metaKey || event.ctrlKey) return;
                                            event.stopPropagation();
                                            setExtraIndex(null);
                                            onSelect?.(part.type === 'reading' ? selectId : part.id, part.url);
                                        }}
                                        className={`relative h-full w-11 shrink-0 ${at ? 'border-l border-gray-200' : ''} ${shown ? 'outline outline-2 -outline-offset-2 outline-purple-500' : ''}`}
                                    >
                                        <Thumb side={part} className="h-full w-full object-cover" />
                                    </span>
                                );
                            }) : (
                                <>
                                    <Thumb side={left} className="h-full object-cover" style={{ width: right ? '50%' : '100%' }} />
                                    {right && <Thumb side={right} className="h-full w-1/2 border-l border-gray-200 object-cover" />}
                                </>
                            )}
                            <span className="absolute bottom-0 right-0 rounded-tl bg-black/55 px-1 text-[9px] text-white">
                                {index + 1}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="flex shrink-0 flex-col gap-1">
                <div className="flex items-center gap-1">
                    {canPair && (
                        <button
                            type="button"
                            data-action-button
                            onClick={() => publishImages(pairAt(images, pairIndex))}
                            className="rounded-lg border border-purple-200 bg-white px-2 py-1.5 text-[11px] font-medium text-purple-700 hover:bg-purple-50"
                            title={selectedPair != null ? 'Pair the two selected frames' : 'Pair this frame with the next'}
                        >
                            Pair
                        </button>
                    )}
                    {isActiveSpread && (
                        <button
                            type="button"
                            data-action-button
                            onClick={() => publishImages(unpairAt(images, activeIndex))}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                            title="Split this spread into two frames"
                        >
                            Unpair
                        </button>
                    )}
                    {overlapping && (
                        <button
                            type="button"
                            data-action-button
                            onClick={() => putOnTop(1 - onTop)}
                            className="flex items-center gap-1 rounded-lg border border-purple-200 bg-white px-2 py-1.5 text-[11px] font-medium text-purple-700 hover:bg-purple-50"
                            title={`The ${topName} image is on top · click to put the ${otherName} one on top`}
                        >
                            <SpreadLayerIcon onTop={onTop} className="h-3.5 w-3.5" />
                            {onTop === 0 ? 'Left on top' : 'Right on top'}
                        </button>
                    )}
                    <button
                        type="button"
                        data-action-button
                        onClick={() => postEditorial({ action: 'arrange', focusIndex: activeIndex >= 0 ? activeIndex : 0 })}
                        className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                        title="Edit crops, scale, and captions in the full sequence editor"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        data-action-button
                        onClick={() => postEditorial({ action: 'arrange' })}
                        className="rounded-lg bg-purple-600 px-2.5 py-1.5 text-[11px] font-medium text-white hover:bg-purple-700"
                        title="Open the full sequence editor"
                    >
                        Arrange
                    </button>
                </div>
                <div className="flex items-center gap-1">
                    {boxSlide && <AddBoxButtons compact count={slideBoxes.length} onAdd={addSlideBox} />}
                    <button
                        type="button"
                        tabIndex={-1}
                        data-action-button
                        onClick={addBlankPage}
                        className="rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                        title="A page with no picture, for text and other blocks. Pair it with a picture to make a spread."
                    >
                        + Blank page
                    </button>
                    <button
                        type="button"
                        tabIndex={-1}
                        data-action-button
                        onClick={addBlankSpread}
                        className="rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                        title="Two pages with no pictures, for text and other blocks."
                    >
                        + Blank spread
                    </button>
                </div>
            </div>
        </div>
        </OutsideFrame>
    );
}

EditorialFilmstrip.propTypes = {
    images: PropTypes.array,
    files: PropTypes.object,
    pagePath: PropTypes.string,
    pages: PropTypes.object,
    currentImageId: PropTypes.string,
    /** The phone text page of the current slide is showing. */
    currentReading: PropTypes.bool,
    onSelect: PropTypes.func,
};
